import { Api } from "../../Api";
import { DataFrame, Method, Status } from "../../DataFrame";
import { ExalusConnectionService } from "../ExalusConnectionService";
import { FieldChangeResult, FieldChangeResultType, ResponseResult } from "../FieldChangeResult";
import { IExalusConnectionService } from "../IExalusConnectionService";
import { ConnectedApp, IConnectedAppsService, UpsertConnectedAppResult } from "./IConnectedAppsService";
import { LoggerService } from "../Logging/LoggerService";
import { ILoggerService } from "../Logging/ILoggerService";
import { IWebApiCacheService } from "../WebApi/IWebApiCacheService";
import { WebApiCacheService } from "../WebApi/WebApiCacheService";

export class ConnectedAppsService implements IConnectedAppsService {
	public static readonly ServiceName: string = "ConnectedAppsService";
	private readonly _connection: IExalusConnectionService = Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName);
	private readonly _log: ILoggerService = Api.Get<ILoggerService>(LoggerService.ServiceName);
	private readonly _cache: IWebApiCacheService = Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName);

	GetServiceName(): string {
		return ConnectedAppsService.ServiceName;
	}

	async IsSupportedAsync(): Promise<boolean> {
		let info = await this._connection.SendAndWaitForResponseAsync<any>(new CheckIfSupportedRequest(), 2000, false);
		
		this._log.Debug(ConnectedAppsService.ServiceName, `IsSupportedAsync: ${info?.Status} resp: ${JSON.stringify(info)}`);

		if (info == null)
			return false;

		else if (info.Status == Status.WrongData)
			return true;

		return false;
	}

	async GetConnectedAppsAsync(): Promise<Status | ConnectedApp[]> {
		let response = await this._connection.SendAndWaitForResponseAsync<ConnectedApp[]>(new GetConnectedAppsRequest(), 2000, true);

		switch (response?.Status) {
			case Status.OK:
				return response?.Data as any as ConnectedApp[];

			default:
				return response?.Status as Status;
		}
	}

	async UpsertConnectedAppAsync(app: ConnectedApp): Promise<ResponseResult<UpsertConnectedAppResult>> {
		let response = await this._connection.SendAndWaitForResponseAsync<string>(new UpsertConnectedAppRequest(app), 2000, false);

		switch (response?.Status) {
			case Status.OK:
				this._cache.Remove(new GetConnectedAppsRequest());
				return new ResponseResult<UpsertConnectedAppResult>(UpsertConnectedAppResult.Changed, "");

			case Status.WrongData:
				switch (response.Data as string) {
					case "AccessTokenToShort":
						return new ResponseResult(UpsertConnectedAppResult.AccessTokenToShort, response.Data as string);

					case "AnotherAppWithGivenAccessTokenAlreadyExists":
						return new ResponseResult(UpsertConnectedAppResult.AnotherAppWithGivenAccessTokenAlreadyExists, response.Data as string);

					case "AppGuidDoesNotExists":
						return new ResponseResult(UpsertConnectedAppResult.AppGuidDoesNotExists, response.Data as string);

					case "AppNameToShort":
						return new ResponseResult(UpsertConnectedAppResult.AppNameToShort, response.Data as string);

					case "AppUrlToShort":
						return new ResponseResult(UpsertConnectedAppResult.AppUrlToShort, response.Data as string);

					case "AppUserIdToShort":
						return new ResponseResult(UpsertConnectedAppResult.AppUserIdToShort, response.Data as string);

					case "AppUserNameToShort":
						return new ResponseResult(UpsertConnectedAppResult.AppUserNameToShort, response.Data as string);

					default:
						return new ResponseResult(UpsertConnectedAppResult.UnkownError, response.Data as string);
				}

			case Status.ResourceDoesNotExists:
			case Status.ResourceIsNotAvailable:
				return new ResponseResult(UpsertConnectedAppResult.ResourceIsNotAvailable, response.Data as string);

			default:
				return new ResponseResult(UpsertConnectedAppResult.UnkownError, response.Data as string);
		}
	}

	async RemoveConnectedAppAsync(app: ConnectedApp): Promise<Status> {
		let response = await this._connection.SendAndWaitForResponseAsync<ConnectedApp[]>(new DeleteConnectedAppRequest(app.Guid), 2000, false);

		if (response?.Status === Status.OK)
			this._cache.Remove(new GetConnectedAppsRequest());

		return response?.Status as Status;
	}
}

class CheckIfSupportedRequest extends DataFrame<any>
{
	constructor() {
		super();
		this.Resource = "/applications/app/auth";
		this.Method = Method.Put;
		this.Data = "";
	}
}


class GetConnectedAppsRequest extends DataFrame<any>
{
	constructor() {
		super();
		this.Resource = "/applications/get";
		this.Method = Method.Get;
	}
}

class DeleteConnectedAppRequest extends DataFrame<string>
{
	constructor(appGuid: string) {
		super();
		this.Resource = "/applications/app/delete";
		this.Method = Method.Delete;
		this.Data = appGuid;
	}
}

class UpsertConnectedAppRequest extends DataFrame<ConnectedApp>
{
	constructor(app: ConnectedApp) {
		super();
		this.Resource = "/applications/app/upsert";
		this.Method = Method.Put;
		this.Data = app;
	}
}
